import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BattererSessionRecord {
  id: number;
  session_date: string;
  session_time: string | null;
  session_status: string;
  dynamics: string | null;
  group_behavior: string | null;
  individual_behavior: string | null;
  program_name: string | null;
}

interface BattererSessionFormProps {
  initial?: BattererSessionRecord;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BattererSessionForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: BattererSessionFormProps) {
  const [sessionDate, setSessionDate] = useState(initial?.session_date ?? "");
  const [sessionTime, setSessionTime] = useState(initial?.session_time ?? "");
  const [sessionStatus, setSessionStatus] = useState(initial?.session_status ?? "present");
  const [dynamics, setDynamics] = useState(initial?.dynamics ?? "");
  const [groupBehavior, setGroupBehavior] = useState(initial?.group_behavior ?? "");
  const [individualBehavior, setIndividualBehavior] = useState(initial?.individual_behavior ?? "");
  const [programName, setProgramName] = useState(initial?.program_name ?? "");

  function handleSubmit() {
    onSubmit({
      sessionDate: sessionDate || undefined,
      sessionTime: sessionTime || undefined,
      sessionStatus,
      dynamics: dynamics || undefined,
      groupBehavior: groupBehavior || undefined,
      individualBehavior: individualBehavior || undefined,
      programName: programName || undefined,
    });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Session Date"
          type="date"
          value={sessionDate}
          onChange={(e) => setSessionDate(e.target.value)}
          required
        />
        <Input
          label="Session Time"
          type="time"
          value={sessionTime}
          onChange={(e) => setSessionTime(e.target.value)}
        />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Status</label>
          <select
            value={sessionStatus}
            onChange={(e) => setSessionStatus(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="excused">Excused</option>
            <option value="late">Late</option>
          </select>
        </div>
      </div>

      <Input
        label="Program Name"
        value={programName}
        onChange={(e) => setProgramName(e.target.value)}
        placeholder="e.g., 26-Week BIP"
      />

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Dynamics</label>
        <textarea
          value={dynamics}
          onChange={(e) => setDynamics(e.target.value)}
          rows={2}
          placeholder="Session dynamics..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Group Behavior</label>
        <textarea
          value={groupBehavior}
          onChange={(e) => setGroupBehavior(e.target.value)}
          rows={2}
          placeholder="Group behavior observations..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Individual Behavior</label>
        <textarea
          value={individualBehavior}
          onChange={(e) => setIndividualBehavior(e.target.value)}
          rows={2}
          placeholder="Individual behavior observations..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Session" : "Create Session"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
